import React from 'react';
import {
    Box,
    Typography,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputAdornment,
    Alert,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { OnboardingData } from '../CompanyOnboardingWizard';

interface Props {
    data: OnboardingData;
    updateData: (updates: Partial<OnboardingData>) => void;
    errors: Record<string, string>;
}

// Architektur post v0.3.0: Demos sind Sales-Werkzeug und leben auf Prod
// (`<slug>.demo.<main>`). Stage-Cluster wird fuer interne Tests genutzt
// (`<slug>.demo-stage.<main>`). Production-Tenants liegen auf Prod
// unter `<slug>.app.<main>`. Wir erkennen den Cluster ueber die
// REACT_APP_SSO_URL des Builds — ein Stage-Build zeigt auf
// stage.sso.<main>.
const isStageBuild = (process.env.REACT_APP_SSO_URL || '').includes('stage.');

export const DomainStep: React.FC<Props> = ({ data, updateData, errors }) => {
    const translate = useTranslate();
    const subdomainSuffix = data.isDemoTenant
        ? (isStageBuild
            ? '.demo-stage.generationl-onlineacademy.de'
            : '.demo.generationl-onlineacademy.de')
        : '.app.generationl-onlineacademy.de';

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('companyOnboarding.steps.domainTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('companyOnboarding.steps.domainDescription')}
            </Typography>

            <RadioGroup
                value={data.domainMode}
                onChange={(e) => updateData({ domainMode: e.target.value as 'subdomain' | 'custom' })}
            >
                <FormControlLabel
                    value="subdomain"
                    control={<Radio />}
                    label={translate('companyOnboarding.fields.domainModeSubdomain')}
                />

                {data.domainMode === 'subdomain' && (
                    <Box sx={{ ml: 4, mb: 3 }}>
                        <TextField
                            label={translate('companyOnboarding.fields.subdomainSlug')}
                            value={data.subdomainSlug}
                            onChange={(e) => updateData({ subdomainSlug: e.target.value.toLowerCase() })}
                            fullWidth
                            required
                            placeholder="firma-x"
                            error={!!errors.subdomainSlug}
                            helperText={errors.subdomainSlug || translate('companyOnboarding.fields.subdomainSlugHelp')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {subdomainSuffix}
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {data.subdomainSlug && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Vorschau: <strong>{data.subdomainSlug}{subdomainSuffix}</strong>
                            </Typography>
                        )}
                    </Box>
                )}

                <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label={translate('companyOnboarding.fields.domainModeCustom')}
                />

                {data.domainMode === 'custom' && (
                    <Box sx={{ ml: 4, mb: 3 }}>
                        <TextField
                            label={translate('companyOnboarding.fields.customDomain')}
                            value={data.customDomain}
                            onChange={(e) => updateData({ customDomain: e.target.value.toLowerCase() })}
                            fullWidth
                            required
                            placeholder="academy.firma.de"
                            error={!!errors.customDomain}
                            helperText={errors.customDomain}
                        />
                        {data.customDomain && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {translate('companyOnboarding.fields.customDomainCnameTitle')}
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                                    {data.customDomain}. CNAME app.generationl-onlineacademy.de.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {translate('companyOnboarding.fields.customDomainCnameHelp')}
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}
            </RadioGroup>
        </Box>
    );
};
